import { useState, useEffect } from 'react';
import {
  Dialog,
  Flex,
  Text,
  Button,
  Box,
  Callout,
  Spinner,
  TextField,
  Tabs,
  Badge,
  Card,
  Code,
} from '@radix-ui/themes';
import {
  CheckCircledIcon,
  CrossCircledIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import { useTestSignalWithDataMutation } from '../../graphql/mutations/TestSignalWithData.generated';
import { FingerprintMatchResult, SignalType, TestSignalInput } from '../../types';

interface TestSignalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signalId: string;
  signalName: string;
  signalType?: SignalType;
}

type InputMode = 'transaction' | 'block' | 'log';

export default function TestSignalDialog({
  open,
  onOpenChange,
  signalId,
  signalName,
  signalType,
}: TestSignalDialogProps) {
  const [testSignal, { data, loading, error, reset }] = useTestSignalWithDataMutation();

  // Input state
  const [txHash, setTxHash] = useState('');
  const [blockNumber, setBlockNumber] = useState('');
  const [blockHash, setBlockHash] = useState('');
  const [logTxHash, setLogTxHash] = useState('');
  const [logIndex, setLogIndex] = useState('');
  const [blockInputMode, setBlockInputMode] = useState<'number' | 'hash'>('number');

  // Determine input mode based on signal type
  const getInputMode = (): InputMode => {
    if (!signalType) return 'transaction';
    switch (signalType) {
      case SignalType.Transaction:
        return 'transaction';
      case SignalType.Block:
        return 'block';
      case SignalType.Log:
        return 'log';
      default:
        return 'transaction';
    }
  };

  const inputMode = getInputMode();

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setTxHash('');
      setBlockNumber('');
      setBlockHash('');
      setLogTxHash('');
      setLogIndex('');
      reset();
    }
  }, [open, reset]);

  const buildInput = (): TestSignalInput | null => {
    switch (inputMode) {
      case 'transaction':
        if (!txHash.trim()) return null;
        return { transaction: { txHash: txHash.trim() } };
      case 'block':
        if (blockInputMode === 'number') {
          const num = parseInt(blockNumber, 10);
          if (isNaN(num)) return null;
          return { block: { blockNumber: num } };
        } else {
          if (!blockHash.trim()) return null;
          return { block: { blockHash: blockHash.trim() } };
        }
      case 'log':
        if (!logTxHash.trim() || !logIndex.trim()) return null;
        const idx = parseInt(logIndex, 10);
        if (isNaN(idx)) return null;
        return { log: { txHash: logTxHash.trim(), logIndex: idx } };
      default:
        return null;
    }
  };

  const isInputValid = (): boolean => {
    return buildInput() !== null;
  };

  const handleTest = async () => {
    const input = buildInput();
    if (!input) return;

    try {
      await testSignal({
        variables: { signalId, input },
      });
    } catch {
      // Error is captured in the mutation result
    }
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const result = data?.testSignalWithData;
  const hasResult = result !== undefined;
  const isSuccess = result?.overallMatch === true && !result?.error;

  const getSignalTypeLabel = (): string => {
    switch (signalType) {
      case SignalType.Transaction:
        return 'Transaction';
      case SignalType.Block:
        return 'Block';
      case SignalType.Log:
        return 'Log/Event';
      default:
        return 'Signal';
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Content style={{ maxWidth: 650 }}>
        <Dialog.Title>Test Signal</Dialog.Title>
        <Dialog.Description size="2" mb="4" color="gray">
          Test "{signalName}" against real blockchain data
        </Dialog.Description>

        {!hasResult && !loading && (
          <>
            <Callout.Root color="blue" mb="4">
              <Callout.Icon>
                <InfoCircledIcon />
              </Callout.Icon>
              <Callout.Text>
                Enter {getSignalTypeLabel().toLowerCase()} data to test your signal's fingerprints
                against real blockchain data.
              </Callout.Text>
            </Callout.Root>

            {/* Transaction Input */}
            {inputMode === 'transaction' && (
              <Box mb="4">
                <Text as="label" size="2" weight="medium" mb="2" style={{ display: 'block' }}>
                  Transaction Hash
                </Text>
                <TextField.Root
                  placeholder="0x..."
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                />
                <Text size="1" color="gray" mt="1">
                  Enter the full transaction hash (0x-prefixed)
                </Text>
              </Box>
            )}

            {/* Block Input */}
            {inputMode === 'block' && (
              <Box mb="4">
                <Tabs.Root value={blockInputMode} onValueChange={(v) => setBlockInputMode(v as 'number' | 'hash')}>
                  <Tabs.List size="1" mb="3">
                    <Tabs.Trigger value="number">Block Number</Tabs.Trigger>
                    <Tabs.Trigger value="hash">Block Hash</Tabs.Trigger>
                  </Tabs.List>

                  {blockInputMode === 'number' ? (
                    <Box>
                      <TextField.Root
                        type="number"
                        placeholder="e.g., 18500000"
                        value={blockNumber}
                        onChange={(e) => setBlockNumber(e.target.value)}
                      />
                      <Text size="1" color="gray" mt="1">
                        Enter the block number
                      </Text>
                    </Box>
                  ) : (
                    <Box>
                      <TextField.Root
                        placeholder="0x..."
                        value={blockHash}
                        onChange={(e) => setBlockHash(e.target.value)}
                      />
                      <Text size="1" color="gray" mt="1">
                        Enter the block hash (0x-prefixed)
                      </Text>
                    </Box>
                  )}
                </Tabs.Root>
              </Box>
            )}

            {/* Log Input */}
            {inputMode === 'log' && (
              <Box mb="4">
                <Box mb="3">
                  <Text as="label" size="2" weight="medium" mb="2" style={{ display: 'block' }}>
                    Transaction Hash
                  </Text>
                  <TextField.Root
                    placeholder="0x..."
                    value={logTxHash}
                    onChange={(e) => setLogTxHash(e.target.value)}
                  />
                </Box>
                <Box>
                  <Text as="label" size="2" weight="medium" mb="2" style={{ display: 'block' }}>
                    Log Index
                  </Text>
                  <TextField.Root
                    type="number"
                    placeholder="e.g., 0"
                    value={logIndex}
                    onChange={(e) => setLogIndex(e.target.value)}
                  />
                  <Text size="1" color="gray" mt="1">
                    The index of the log within the transaction (starts at 0)
                  </Text>
                </Box>
              </Box>
            )}
          </>
        )}

        {loading && (
          <Flex align="center" justify="center" py="6" gap="3">
            <Spinner size="2" />
            <Text color="gray">Fetching blockchain data and testing...</Text>
          </Flex>
        )}

        {(error || result?.error) && (
          <Callout.Root color="red" mb="4">
            <Callout.Icon>
              <CrossCircledIcon />
            </Callout.Icon>
            <Callout.Text>
              <Text weight="bold">Error:</Text> {error?.message || result?.error}
            </Callout.Text>
          </Callout.Root>
        )}

        {hasResult && !result?.error && (
          <>
            <Callout.Root color={isSuccess ? 'green' : 'amber'} mb="4">
              <Callout.Icon>
                {isSuccess ? <CheckCircledIcon /> : <CrossCircledIcon />}
              </Callout.Icon>
              <Callout.Text>
                <Box>
                  <Text weight="bold">
                    {isSuccess ? 'All fingerprints matched!' : 'Some fingerprints did not match'}
                  </Text>
                </Box>
                {result.fetchedDataSummary && (
                  <Box mt="1">
                    <Text size="1" color="gray">{result.fetchedDataSummary}</Text>
                  </Box>
                )}
              </Callout.Text>
            </Callout.Root>

            {result.fingerprintResults.length > 0 && (
              <Box mb="4">
                <Text size="2" weight="medium" mb="2" style={{ display: 'block' }}>
                  Fingerprint Results
                </Text>
                <Flex direction="column" gap="2">
                  {result.fingerprintResults.map((fp: FingerprintMatchResult, index: number) => (
                    <Card key={index} size="1" style={{ padding: '12px' }}>
                      <Flex align="start" justify="between" gap="3">
                        <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
                          <Flex align="center" gap="2">
                            <Badge
                              color={fp.matched ? 'green' : 'red'}
                              variant="soft"
                              size="1"
                            >
                              {fp.matched ? 'Match' : 'Mismatch'}
                            </Badge>
                            <Code size="1" weight="bold">{fp.field}</Code>
                          </Flex>
                          <Flex gap="4" mt="1">
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text size="1" color="gray">Expected:</Text>
                              <Code size="1" style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {fp.expected}
                              </Code>
                            </Box>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text size="1" color="gray">Actual:</Text>
                              <Code size="1" color={fp.matched ? undefined : 'red'} style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {fp.actual}
                              </Code>
                            </Box>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Card>
                  ))}
                </Flex>
              </Box>
            )}
          </>
        )}

        <Flex gap="3" mt="4" justify="end">
          <Button variant="soft" color="gray" onClick={handleClose}>
            {hasResult ? 'Close' : 'Cancel'}
          </Button>
          {!hasResult && (
            <Button onClick={handleTest} disabled={loading || !isInputValid()}>
              {loading ? 'Testing...' : 'Run Test'}
            </Button>
          )}
          {hasResult && (
            <Button variant="soft" onClick={() => reset()}>
              Test Another
            </Button>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
